import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback, getViewParameterValue } from '@theme/utilities';

/**
 * 指定した選択値だけをピッカーに反映する。
 * 未選択の項目は、再取得したHTMLでも未選択に戻す。
 */
function applyExplicitSelections(picker, selectedIds) {
  const selected = new Set(selectedIds);

  picker.querySelectorAll('[data-required-option]').forEach((group) => {
    group.querySelectorAll('input[type="radio"]').forEach((input) => {
      const isSelected = selected.has(input.dataset.optionValueId);

      input.checked = isSelected;
      input.toggleAttribute('checked', isSelected);
      input.dataset.currentChecked = String(isSelected);
      input.dataset.previousChecked = 'false';
    });

    const select = group.querySelector('select');

    if (select) {
      let placeholder = select.querySelector(
        'option[data-selection-placeholder]'
      );

      if (!placeholder) {
        placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.textContent = '選択してください';
        placeholder.dataset.selectionPlaceholder = '';
        select.prepend(placeholder);
      }

      let hasSelection = false;

      Array.from(select.options).forEach((option) => {
        const isSelected =
          Boolean(option.dataset.optionValueId) &&
          selected.has(option.dataset.optionValueId);

        option.selected = isSelected;
        option.toggleAttribute('selected', isSelected);

        if (isSelected) hasSelection = true;
      });

      placeholder.selected = !hasSelection;
      placeholder.toggleAttribute('selected', !hasSelection);

      if (!hasSelection) select.value = '';
    }

    const checked = group.querySelector('input[type="radio"]:checked');
    const selectedOption = select?.selectedOptions[0];

    const value = checked?.value ||
      (selectedOption?.dataset.optionValueId ? selectedOption.value : '');

    group.querySelectorAll(
      '.variant-option__swatch-value, .variant-option__accordion-value'
    ).forEach((label) => {
      label.textContent = value || '未選択';
    });

    group.querySelectorAll(
      '[data-variant-selection-error]'
    ).forEach((error) => error.remove());

    group.removeAttribute('aria-invalid');
    group.querySelectorAll('input, select').forEach((input) => {
      input.removeAttribute('aria-invalid');
    });
  });
}

/**
* 初期未選択時だけ、サーバー側の仮選択による売り切れ表示を解除する。
* 実在庫や購入可否の判定は変更しない。
*/
function resetInitialAvailability(picker) {
  picker.querySelectorAll('[data-required-option]').forEach((group) => {
    group.querySelectorAll('input[type="radio"]').forEach((input) => {
      input.dataset.optionAvailable = 'true';
      input.removeAttribute('aria-disabled');

      const label = input.closest('.variant-option__button-label');
      if (!label) return;

      label.querySelectorAll('.variant-option__strikethrough').forEach((svg) => {
        svg.remove();
      });

      // Liquidで省略されていた選択背景も補う。
      if (
        !label.classList.contains('variant-option__button-label--has-swatch') &&
        !label.querySelector('.variant-option__button-label__pill')
      ) {
        const pill = document.createElement('span');
        pill.className = 'variant-option__button-label__pill';
        input.after(pill);
      }
    });
  });
}

const progressiveInventoryRequests = new Map();

async function loadProgressiveInventory(picker) {
  const productUrl = picker.dataset.productUrl;
  const productId = picker.dataset.productId;
  const source = picker.querySelector('[data-progressive-variants]');

  if (!productUrl || !productId || !source) {
    throw new Error('在庫判定用の要素が見つかりません。');
  }

  const baseUrl = new URL(productUrl, window.location.origin);
  const key = `${baseUrl.pathname}:${productId}`;

  if (!progressiveInventoryRequests.has(key)) {
    const request = (async () => {
      async function getPage(page) {
        const url = new URL(baseUrl);

        url.search = '';
        url.searchParams.set('view', 'progressive-inventory');
        url.searchParams.set('page', String(page));

        const response = await fetch(url.toString(), {
          credentials: 'same-origin',
        });

        if (!response.ok) {
          throw new Error(`在庫データ取得エラー: ${response.status}`);
        }

        const data = await response.json();

        if (
          String(data.productId) !== String(productId) ||
          !Array.isArray(data.variants) ||
          data.page !== page ||
          !Number.isInteger(data.pages) ||
          !Number.isInteger(data.total)
        ) {
          throw new Error('在庫データの形式が正しくありません。');
        }

        return data;
      }

      const first = await getPage(1);

      if (first.pages < 1 || first.pages > 20) {
        throw new Error('在庫データのページ数を確認してください。');
      }

      const variants = [...first.variants];

      // 一度に大量の通信を行わず、順番に取得する。
      for (let page = 2; page <= first.pages; page += 1) {
        const next = await getPage(page);

        if (
          next.total !== first.total ||
          next.pages !== first.pages
        ) {
          throw new Error('取得中に商品データが変わりました。再読み込みしてください。');
        }

        variants.push(...next.variants);
      }

      if (variants.length !== first.total) {
        throw new Error(
          `在庫データが不足しています: ${variants.length}/${first.total}`
        );
      }

      return { total: first.total, variants };
    })();

    progressiveInventoryRequests.set(key, request);

    request.catch(() => {
      progressiveInventoryRequests.delete(key);
    });
  }

  const data = await progressiveInventoryRequests.get(key);

  // 取得中に別商品へ切り替わった場合は反映しない。
  if (
    !picker.isConnected ||
    picker.dataset.productId !== productId
  ) {
    return false;
  }

  source.textContent = JSON.stringify(data);
  return true;
}

/**
* 商品管理上の順序で、上位の選択に基づく購入可否を反映する。
* 在庫なしも非表示にはせず、既存のグレーアウト表示を使う。
*/
function updateProgressiveAvailability(picker, changedGroup = null) {
  const source = picker.querySelector('[data-progressive-variants]');
  if (!source) return;

  let data;

  try {
    data = JSON.parse(source.textContent || '{}');
  } catch (error) {
    console.error('在庫判定用データを読み込めませんでした。', error);
    return;
  }

  if (
    !Array.isArray(data.variants) ||
    !Number.isInteger(data.total) ||
    data.total !== data.variants.length
  ) {
    console.error(
      '全バリエーションを取得できないため、段階的な在庫判定を中止しました。'
    );
    return;
  }

  const groups = Array.from(
    picker.querySelectorAll('[data-required-option]')
  );

  // 別商品への切り替えを含むピッカーは今回の処理対象外。
  if (
    Array.from(
      picker.querySelectorAll('[data-connected-product-url]')
    ).some((element) => element.dataset.connectedProductUrl)
  ) {
    return;
  }

  const changedIndex = groups.indexOf(changedGroup);
  const prefix = [];

  groups.forEach((group, index) => {
    const select = group.querySelector('select');
    const choices = select
      ? Array.from(
          select.querySelectorAll('option[data-option-value-id]')
        )
      : Array.from(group.querySelectorAll('input[type="radio"]'));

    const getSelected = () => choices.find((choice) =>
      choice instanceof HTMLOptionElement
        ? choice.selected
        : choice.checked
    );

    // 上位項目がすべて選択されるまでは絞り込まない。
    // 最初の項目は常に通常表示。
    const canJudge =
      index > 0 &&
      prefix.length === index &&
      prefix.every((value) => value !== null);

    choices.forEach((choice) => {

    const hasAvailableCombination = data.variants.some((variant) =>
      variant.available &&
      variant.options[index] === choice.value &&
      prefix.every(
        (value, position) => variant.options[position] === value
      )
    );

    const available =
      index === 0 ||
      !canJudge ||
      hasAvailableCombination;
   
   

      choice.dataset.optionAvailable = String(available);

      if (available) {
        choice.removeAttribute('aria-disabled');
      } else {
        choice.setAttribute('aria-disabled', 'true');
      }

      if (choice instanceof HTMLInputElement) {
        const label = choice.closest('.variant-option__button-label');

        if (label) {
          // 元のLiquidと同じく、選択背景は購入可能なボタンだけに置く。
          if (
            !label.classList.contains(
              'variant-option__button-label--has-swatch'
            )
          ) {
            const pill = label.querySelector(
              '.variant-option__button-label__pill'
            );

            if (!available) {
              pill?.remove();
            } else if (!pill) {
              const newPill = document.createElement('span');
              newPill.className =
                'variant-option__button-label__pill';
              choice.after(newPill);
            }
          }

          const existing = label.querySelector(
            '.variant-option__strikethrough'
          );


          if (available) {
            existing?.remove();
          } else if (!existing) {
            const namespace = 'http://www.w3.org/2000/svg';
            const svg = document.createElementNS(namespace, 'svg');

            svg.setAttribute('viewBox', '0 0 100 46');
            svg.setAttribute('preserveAspectRatio', 'none');
            svg.setAttribute('class', 'variant-option__strikethrough');
            svg.setAttribute('aria-hidden', 'true');
            svg.setAttribute('focusable', 'false');

            for (let index = 0; index < 2; index += 1) {
              const line = document.createElementNS(namespace, 'line');

              line.setAttribute('x1', '100');
              line.setAttribute('y1', '0');
              line.setAttribute('x2', '0');
              line.setAttribute('y2', '46');
              line.setAttribute('vector-effect', 'non-scaling-stroke');

              svg.append(line);
            }

            label.append(svg);
          }
        }
      }
   
      if (choice instanceof HTMLOptionElement) {
        // 初回のラベルを保存し、売り切れ表記の重複を防ぐ。
        if (!choice.dataset.progressiveLabel) {
          choice.dataset.progressiveLabel = choice.value;
        }

        choice.textContent = available
          ? choice.dataset.progressiveLabel
          : `${choice.dataset.progressiveLabel} - 売り切れ`;
      }
    });

    const selected = getSelected();

    // 上位の変更により無効になった、後続の選択だけを解除する。
    if (
      changedIndex >= 0 &&
      index > changedIndex &&
      selected?.dataset.optionAvailable === 'false'
    ) {
      if (selected instanceof HTMLOptionElement) {
        selected.selected = false;
        selected.removeAttribute('selected');

        const placeholder = select.querySelector(
          'option[data-selection-placeholder]'
        );

        if (placeholder) {
          placeholder.selected = true;
          placeholder.setAttribute('selected', 'selected');
        }

        select.value = '';
      } else {
        selected.checked = false;
        selected.removeAttribute('checked');
      }
    }

    prefix.push(getSelected()?.value ?? null);
  });

  // 表示ラベルと選択追跡用の属性を選択状態に合わせる。
  const selectedIds = groups.flatMap((group) => {
    const selected = group.querySelector(
      'input[type="radio"]:checked, ' +
      'select option:checked[data-option-value-id]'
    );

    return selected?.dataset.optionValueId
      ? [selected.dataset.optionValueId]
      : [];
  });

  applyExplicitSelections(picker, selectedIds);
}
   
   
/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  connectedCallback() {
    super.connectedCallback();

    if (this.#usesExplicitSelection()) {
      const urlVariant = new URL(window.location.href)
        .searchParams.get('variant');

      let renderedVariant = null;

      try {
        renderedVariant = JSON.parse(
          this.querySelector('script[type="application/json"]')
            ?.textContent || 'null'
        );
      } catch (error) {
        console.error('Variant information could not be read:', error);
      }

      // 有効なバリアント指定URLでは、その選択を維持する。
      const keepUrlSelection =
        Boolean(urlVariant) &&
        String(renderedVariant?.id) === urlVariant;

      const selectedIds = keepUrlSelection
        ? this.selectedOptionsValues
        : [];

      applyExplicitSelections(this, selectedIds);

      if (!keepUrlSelection) {
        resetInitialAvailability(this);
      }
   
    }

    this.#rebuildSelectionTracking();

    this.addEventListener('change', this.variantChanged.bind(this));

    if (this.#usesExplicitSelection()) {
      loadProgressiveInventory(this)
        .then((loaded) => {
          if (!loaded) return;

          // 読み込み中に選択された値も、全件データで再判定する。
          const firstGroup = this.querySelector('[data-required-option]');

          updateProgressiveAvailability(this, firstGroup);
          this.#rebuildSelectionTracking();
        })
        .catch((error) => {
          console.error('段階的な在庫判定を開始できませんでした。', error);
        });
    }
   
  }

  #usesExplicitSelection() {
    return (
      this.dataset.requireExplicitSelection === 'true' &&
      !this.closest('product-card, quick-add-dialog')
    );
  }

  #rebuildSelectionTracking() {
    this.#radios = [];
    this.#checkedIndices = [];

    const fieldsets = this.refs.fieldsets || [];

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(
        fieldset.querySelectorAll('input[type="radio"]')
      );

      this.#radios.push(radios);

      const checkedIndex = radios.findIndex((radio) => radio.checked);

      // 未選択でも項目ごとの配列を作り、添字をずらさない。
      this.#checkedIndices.push(
        checkedIndex === -1 ? [] : [checkedIndex]
      );
    });
}

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);

    if (this.#usesExplicitSelection()) {
      applyExplicitSelections(this, this.selectedOptionsValues);

      updateProgressiveAvailability(
        this,
        event.target.closest('[data-required-option]')
      );

      this.#rebuildSelectionTracking();

      const groups = Array.from(
   
        this.querySelectorAll('[data-required-option]')
      );

      const allSelected =
        groups.length > 0 &&
        groups.every((group) => {
          const select = group.querySelector('select');

          return select
            ? Boolean(select.value)
            : Boolean(group.querySelector('input[type="radio"]:checked'));
        });

      if (!allSelected) {
        this.#abortController?.abort();
        this.dataset.selectionUpdating = 'false';
        return;
      }

      this.dataset.selectionUpdating = 'true';
    }

    this.dispatchEvent(
      new VariantSelectedEvent({
        id: selectedOption.dataset.optionValueId ?? '',
      })
    );


    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), morphElementSelector);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
            fieldset.style.setProperty(
              '--pill-width-current',
              `${radios[newCurrentIndex].parentElement?.offsetWidth || 0}px`
            );
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
            fieldset.style.setProperty(
              '--pill-width-previous',
              `${radios[newPreviousIndex].parentElement?.offsetWidth || 0}px`
            );
          }
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    // 商品ページ本体も自分のセクションだけ取り直す。
    //
    // 上流は PDP 本体をどの分岐にも入れていないため、色・サイズを1回タップするたびに
    // 商品ページの HTML 全体を取得していた（本番実測 2026-08-26 で 1,297KB。
    // section_id を付けると同じ内容が 356KB）。取得後は DOMParser でパースして
    // morph するので、パース費用もそのぶん乗っていた。
    //
    // 適用条件を絞る:
    //   - このテンプレートの商品と一致している（テンプレート違いは従来どおり全文）
    //   - product-card / quick-add ダイアログの中ではない（上のマップで処理済み）
    //   - 組み合わせ商品で別商品へ切り替わる場合は除く。この場合 handleChange が
    //     morph 対象を `main` にするため、セクション単体では足りない
    //
    // variantUpdate に載せた HTML を読む側（product-price / product-form-component /
    // quantity-selector-component / sticky-add-to-cart / product-store-inventory /
    // media-gallery）は本番実測で全部このセクションの中にあることを確認済み。
    const ownSectionId = this.dataset.sectionId;
    const switchesProduct = Boolean(selectedOption.dataset.connectedProductUrl);

    if (
      ownSectionId &&
      !switchesProduct &&
      this.dataset.templateProductMatch === 'true' &&
      !this.closest('product-card') &&
      !this.closest('quick-add-dialog')
    ) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${ownSectionId}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    const explicitSelection = this.#usesExplicitSelection();
    const selectedIds = explicitSelection
      ? this.selectedOptionsValues
      : [];

    if (explicitSelection) {
      const currentSource = this.querySelector(
        '[data-progressive-variants]'
      );
      const nextSource = newVariantPickerSource.querySelector(
        '[data-progressive-variants]'
      );

      // 同じ商品なら、取得済みの全件データを引き継ぐ。
      if (!newProduct && currentSource && nextSource) {
        nextSource.textContent = currentSource.textContent;
      }

      applyExplicitSelections(newVariantPickerSource, selectedIds);
      resetInitialAvailability(newVariantPickerSource);
      updateProgressiveAvailability(newVariantPickerSource);
    }

   

    morph(this, newVariantPickerSource);

    if (explicitSelection) {
      this.dataset.selectionUpdating = 'false';
    }

    this.#rebuildSelectionTracking();


    return newProduct;
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector(
      'select option[selected][data-option-value-id], ' +
      'fieldset input:checked[data-option-value-id]'
    );

    if (
      !(selectedOption instanceof HTMLInputElement ||
        selectedOption instanceof HTMLOptionElement)
    ) {
      return undefined;
    }

    return selectedOption;
  }


  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    const selectedOptions = Array.from(
      this.querySelectorAll(
        'select option[selected][data-option-value-id], ' +
        'fieldset input:checked[data-option-value-id]'
      )
    );

    return selectedOptions
      .map((option) => option.dataset.optionValueId)
      .filter(Boolean);
  }

}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
